import React, { useState, useEffect, useRef } from "react";
import { useSubmission, useFetch } from '../../../hooks/useCallApi';
import { complaintsAPI } from "../../../services/api/admin/complaintsApi";
import FormInput from "../../common/FormInput";
import { Controller } from "react-hook-form";
import { CloudArrowUpIcon, DocumentTextIcon, TrashIcon } from "@heroicons/react/24/outline";

const ComplaintDetailsTab = ({
  control,
  register,
  errors,
  isAnonymous,
  setIsAnonymous,
  setValue,
  uploadedFiles,
  setUploadedFiles,
  searchTerm,
  setSearchTerm,
  watch,
  categories,
  setCategories,
  complaintData,
  setFilesToRemove
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const categoryId = watch("category_id");
  const priorityValue = watch("priority");
  const statusValue = watch("status");

  const { isLoading: categoriesLoading } = useFetch(
    ['complaint-categories-select'],
    (api) => complaintsAPI.selectCategory(),
    {
      onSuccess: (response) => {
        const fetchedCategories = response?.data || [];
        
        const activeCategories = fetchedCategories.filter(cat => cat.status === 'active');
        setCategories(activeCategories.length > 0 ? activeCategories : fetchedCategories);
      }
    }
  );

  const selectedCategory = categories.find(cat => cat.value === parseInt(categoryId));

  useEffect(() => {
    if (selectedCategory && selectedCategory.priority) {
      setValue("priority", selectedCategory.priority, { shouldValidate: true });
    }
  }, [categoryId, selectedCategory, setValue]);

  useEffect(() => {
    if (complaintData?.attachments && complaintData.attachments.length > 0) {
      const existingFiles = complaintData.attachments.map((attachment, index) => ({
        id: `existing-${attachment.id || index}`,
        serverId: attachment.id,
        name: attachment.file_name || attachment.original_name || 'Attachment',
        size: attachment.file_size || 0,
        type: attachment.mime_type || 'application/octet-stream',
        url: attachment.url,
        preview: attachment.url,
        status: 'completed',
        progress: 100,
        isExisting: true 
      }));
      
      setUploadedFiles(prev => [...existingFiles, ...prev.filter(f => !f.isExisting)]);
    }
  }, [complaintData, setUploadedFiles]);

  const searchMutation = useSubmission(
    ["searchResident"],
    (api, searchParams) => complaintsAPI.searchComplainantResident(searchParams),
    {
      onSuccess: (res) => {
        if (res?.data?.data?.searched_resident && Array.isArray(res.data.data.searched_resident)) {
          setSearchResults(res.data.data.searched_resident);
        } else {
          setSearchResults([]);
        }
      },
      onError: (error) => {
        console.error("Search error:", error);
        setSearchResults([]);
      },
    }
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2 || isAnonymous) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      searchMutation.mutate({ search: searchTerm });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, isAnonymous]);

  const handleAnonymousToggle = (checked) => {
    setIsAnonymous(checked);

    if (checked) {
      setValue("complainant_name", "Anonymous", { shouldValidate: true });
      setValue("complainant_purok", "N/A", { shouldValidate: true });
      setValue("resident_id", null, { shouldValidate: true });
      setSelectedResident(null);
      setSearchTerm("");
      setSearchResults([]);
    } else {
      setValue("complainant_name", "", { shouldValidate: true });
      setValue("complainant_purok", "", { shouldValidate: true });
      setValue("resident_id", null, { shouldValidate: true });
      setSelectedResident(null);
      setSearchTerm("");
    }
  };

  const handleResidentSelect = (resident) => {
    setSelectedResident(resident);
    setValue("complainant_name", resident.full_name, { shouldValidate: true });
    setValue("complainant_purok", resident.purok_zone || "", { shouldValidate: true });
    setValue("resident_id", resident.id, { shouldValidate: true });
    setSearchTerm(resident.full_name);
    setShowResults(false);
    setSearchResults([]);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowResults(true);
    
    if (selectedResident) {
      setSelectedResident(null);
      setValue("resident_id", null, { shouldValidate: true });
      setValue("complainant_name", value, { shouldValidate: true });
      setValue("complainant_purok", "", { shouldValidate: true });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (file) => {
    const newFile = {
      id: Math.random().toString(36).substr(2, 9),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      progress: 0,
      status: 'uploading',
      isExisting: false
    };

    setUploadedFiles((prev) => {
      const updated = [...prev, newFile].slice(0, 10);
      return updated;
    });

    simulateFileUpload(newFile.id);
  };

  const simulateFileUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.floor(Math.random() * 15) + 5;
      
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, progress: Math.min(progress, 100) }
            : f
        )
      );

      if (progress >= 100) {
        clearInterval(interval);
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? { ...f, status: 'completed', progress: 100 }
              : f
          )
        );
      }
    }, 300);
  };

  const handleFileRemove = (fileId) => {
    setUploadedFiles((prev) => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      
      if (complaintData && fileToRemove?.serverId) {
        setFilesToRemove(prev => [...prev, fileToRemove.serverId]);
      }
      
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    droppedFiles.forEach(file => handleFileSelect(file));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    selectedFiles.forEach(file => handleFileSelect(file));
    e.target.value = '';
  };

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "normal", label: "Normal" },
    { value: "high", label: "High" },
  ];

  const statusOptions = [
    { value: "submitted", label: "Submitted" },
    { value: "acknowledged", label: "Acknowledged" },
    { value: "assigned", label: "Assigned" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ];

  return (
    <div className="space-y-4 animate-in slide-in-from-left duration-300">
      <input type="hidden" {...register("resident_id")} />

      {/* Complainant Name and Anonymous Checkbox */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1 relative" ref={searchRef}>
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Complainant Name <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="anonymous"
                checked={isAnonymous}
                onChange={(e) => handleAnonymousToggle(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-0"
              />
              <label
                htmlFor="anonymous"
                className="ml-2 text-sm text-gray-600 whitespace-nowrap"
              >
                Anonymously
              </label>
            </div>
          </div>

          <div className="relative">
            <input
              type="text"
              ref={searchInputRef}
              value={searchTerm}
              onChange={handleSearchInputChange}
              onFocus={() => !isAnonymous && setShowResults(true)}
              placeholder={isAnonymous ? "Anonymous" : "Enter complainant name"}
              disabled={isAnonymous}
              className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 ${
                errors.complainant_name
                  ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              } ${
                isAnonymous
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "bg-white text-gray-900"
              } placeholder-gray-400 focus:outline-none focus:ring-1`}
            />

            {/* Loading indicator */}
            {searchMutation.isLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showResults && !isAnonymous && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((resident) => (
                <div
                  key={resident.id}
                  onClick={() => handleResidentSelect(resident)}
                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                >
                  <div className="font-medium text-gray-900">{resident.full_name}</div>
                  <div className="text-sm text-gray-500">
                    {resident.purok_zone || "No Purok"} • {resident.contact_number || "No contact"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {resident.full_address}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results Message */}
          {showResults && !isAnonymous && searchTerm.length >= 2 && !searchMutation.isLoading && searchResults.length === 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
              No residents found matching "{searchTerm}"
            </div>
          )}

          {errors.complainant_name && (
            <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-left duration-300">
              {errors.complainant_name.message}
            </p>
          )}
        </div>

        <FormInput
          label="Purok"
          required
          {...register("complainant_purok", {
            required: !isAnonymous ? "Purok is required" : false
          })}
          error={errors.complainant_purok}
          placeholder="Enter purok here"
          disabled={isAnonymous}
          value={!isAnonymous && selectedResident ? selectedResident.purok_zone || "" : undefined}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register("description", { 
            required: "Description is required",
          })}
          rows={6}
          placeholder="Enter a brief description here"
          className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 min-h-[150px] ${
            errors.description
              ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
              : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          } placeholder-gray-400 focus:outline-none focus:ring-1`}
        />
        {errors.description && (
          <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-left duration-300">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* Complaint Category and Priority Level */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Category Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Complaint Category <span className="text-red-500">*</span>
          </label>
          <Controller
            name="category_id"
            control={control}
            rules={{ required: "Complaint category is required" }}
            render={({ field: { onChange, value } }) => (
              <select
                value={value || ""}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                disabled={categoriesLoading}
                className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 ${
                  errors.category_id
                    ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                } ${categoriesLoading ? "bg-gray-100 cursor-wait" : "bg-white"} placeholder-gray-400 focus:outline-none focus:ring-1`}
              >
                <option value="">
                  {categoriesLoading ? "Loading categories..." : "Select a complaint category"}
                </option>
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.category_id && (
            <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-left duration-300">
              {errors.category_id.message}
            </p>
          )}

        </div>

        {/* Priority Select*/}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority Level <span className="text-red-500">*</span>
          </label>
          <Controller
            name="priority"
            control={control}
            rules={{ required: "Priority level is required" }}
            render={({ field: { onChange, value } }) => (
              <select
                value={value || ""}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                disabled={true} 
                className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 bg-gray-100 cursor-not-allowed ${
                  errors.priority
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200"
                } placeholder-gray-400 focus:outline-none focus:ring-1`}
              >
                <option value="">Priority level</option>
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.priority && (
            <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-left duration-300">
              {errors.priority.message}
            </p>
          )}
        </div>
      </div>

      {/* Status and Landmark */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Status Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field: { onChange, value } }) => (
              <select
                value={value || ""}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 text-gray-900 transition-all duration-200 ${
                  errors.status
                    ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                } placeholder-gray-400 focus:outline-none focus:ring-1`}
              >
                <option value="">Select status</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.status && (
            <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-left duration-300">
              {errors.status.message}
            </p>
          )}
        </div>

        {/* Landmark */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Landmark
          </label>
          <input
            type="text"
            {...register("landmark")}
            placeholder="Enter nearest landmark here"
            className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 ${
              errors.landmark
                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            } placeholder-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.landmark && (
            <p className="text-red-500 text-xs mt-1 animate-in slide-in-from-left duration-300">
              {errors.landmark.message}
            </p>
          )}
        </div>
      </div>

      {/* Supporting Documents */}
      <div className="col-span-1 md:col-span-2 w-full">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Supporting Documents
        </label>

        <div className="space-y-3">
          {/* File List */}
          {uploadedFiles.map((file) => (
            <div key={file.id} className="border rounded-lg bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <div className="min-w-0">
                    {file.preview || file.url ? (
                      <a
                        href={file.url || file.preview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-blue-600 truncate hover:underline block"
                      >
                        {file.name}
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-gray-700 truncate block">
                        {file.name}
                      </span>
                    )}
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <span>
                        {file.progress === 100
                          ? formatFileSize(file.size)
                          : `${formatFileSize(Math.round((file.size * file.progress) / 100))} / ${formatFileSize(file.size)}`}
                      </span>
                      <span className="mx-2">•</span>
                      <span
                        className={`font-medium ${
                          file.progress === 100 ? "text-green-600" : "text-blue-600"
                        }`}
                      >
                        {file.progress === 100 ? (file.isExisting ? "Existing" : "Completed") : "Uploading..."}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleFileRemove(file.id)}
                  className="ml-4 p-2 text-gray-400 hover:text-red-500 rounded-full"
                  type="button"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Progress Bar*/}
              {file.progress < 100 && !file.isExisting && (
                <div className="w-full bg-gray-200 rounded-full h-1 mt-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${file.progress}%` }}
                  />
                </div>
              )}
            </div>
          ))}

          {/* Upload Area */}
          {uploadedFiles.length < 10 && (
            <div
              className="w-full border-2 border-dashed border-gray-200 bg-gray-50 rounded-lg cursor-pointer hover:border-blue-300 transition duration-150 text-center"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="p-10 text-center">
                <CloudArrowUpIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-700">
                  Drag and Drop <span className="font-semibold">Supporting Documents</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  or{" "}
                  <span className="text-blue-600 font-medium hover:underline">
                    browse files
                  </span>{" "}
                  on your computer
                </p>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileInputChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Anonymous Warning */}
      {isAnonymous && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg animate-in slide-in-from-top duration-300">
          <div className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-yellow-600 mr-2 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-xs text-yellow-800">
              You are submitting anonymously. Your identity will not be recorded.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintDetailsTab;